import { useState } from 'react';
import { toast } from 'sonner';
import {
  ecfService,
  type EcfConfig,
  type ServiceCheckResult,
} from '../../../../services/ecf.service';

/**
 * Pestaña "Servicios web": las URLs por las que la DGII y otros contribuyentes nos envían
 * comprobantes. Son las que se registran en el paso 7 de la certificación.
 *
 * Los sufijos (`/fe/recepcion/api/ecf`, etc.) los fija el estándar Emisor-Receptor de la DGII y
 * no se pueden renombrar: si cambian aquí, hay que cambiarlos también en el backend que los
 * sirve. Lo único nuestro es la base, que incluye el RNC del emisor.
 */

interface ServiciosTabProps {
  config: EcfConfig;
}

export default function ServiciosTab({ config }: ServiciosTabProps) {
  const [copied, setCopied] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<ServiceCheckResult | null>(null);

  const urls = config.service_urls;

  const copy = async (value: string, key: string) => {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error('No se pudo copiar al portapapeles');
    }
  };

  /**
   * La comprobación la hace el SERVIDOR, no el navegador: quien tiene que ser alcanzable por la
   * DGII es el backend, y un fetch desde aquí probaría otra cosa (además de chocar con CORS).
   */
  const check = async () => {
    setChecking(true);
    setResult(null);
    try {
      setResult(await ecfService.checkServiceUrls());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudieron comprobar las URLs');
    } finally {
      setChecking(false);
    }
  };

  const filas = urls
    ? [
        { key: 'recepcion', label: 'Recepción de e-CF', url: urls.recepcion },
        { key: 'aprobacion', label: 'Aprobación Comercial', url: urls.aprobacion },
        { key: 'autenticacion', label: 'Autenticación', url: urls.autenticacion },
      ]
    : [];

  return (
    <div className="max-w-4xl">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            <i className="ri-server-line mr-2 text-blue-600"></i>
            Servicios del emisor (URLs)
          </h3>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-xs text-gray-500">
            Estas URLs se registran en el paso 7 de la certificación para que la DGII y otros
            contribuyentes envíen e-CF y aprobaciones a tu empresa. Deben ser accesibles por HTTPS
            desde internet.
          </p>

          {!urls ? (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3 flex items-start gap-2">
              <i className="ri-error-warning-line mt-0.5 shrink-0"></i>
              <span className="text-sm">
                Falta el RNC del emisor para poder construir las URLs. Regístralo en la pestaña
                Emisor y certificado. Registrar en la DGII un dominio que no responde hace fallar
                los pasos 8 al 11, y el portal no explica por qué.
              </span>
            </div>
          ) : (
            <>
              {/* Lo que se teclea literalmente en el portal */}
              <div className="border border-gray-200 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-gray-900">
                  1. Escribe esto en las tres casillas del portal
                </p>
                <p className="text-xs text-gray-500">
                  El portal ya pone el <code className="font-mono">https://</code> a la izquierda y
                  el sufijo fijo a la derecha, así que solo se teclea el host con su ruta. Es el
                  mismo valor en las tres.
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <code className="text-sm font-mono bg-gray-50 border border-gray-200 rounded px-3 py-2 text-gray-800 break-all">
                    {urls.hostPath}
                  </code>
                  <button
                    onClick={() => copy(urls.hostPath, 'hostPath')}
                    className="text-sm text-blue-600 hover:text-blue-900 whitespace-nowrap"
                  >
                    <i className="ri-file-copy-line mr-1"></i>
                    {copied === 'hostPath' ? 'Copiado' : 'Copiar'}
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  Construido con el RNC <span className="font-mono">{urls.rnc}</span>. Confirma que
                  es el de tu empresa.
                </p>
              </div>

              {/* Las URLs completas */}
              <div className="border border-gray-200 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-gray-900 mb-1">
                  2. Así quedan las URLs completas
                </p>
                {filas.map((f) => (
                  <div
                    key={f.key}
                    className="border border-gray-200 rounded-lg px-4 py-2.5 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500">{f.label}</p>
                      <p className="text-sm font-mono text-gray-800 truncate">{f.url}</p>
                    </div>
                    <button
                      onClick={() => copy(f.url, f.key)}
                      className="text-sm text-blue-600 hover:text-blue-900 whitespace-nowrap shrink-0"
                    >
                      <i className="ri-file-copy-line mr-1"></i>
                      {copied === f.key ? 'Copiado' : 'Copiar'}
                    </button>
                  </div>
                ))}
              </div>

              {/* Comprobación */}
              <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium text-gray-900">
                  3. Comprueba que la DGII podrá alcanzarte
                </p>
                <button
                  onClick={check}
                  disabled={checking}
                  className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50"
                >
                  <i
                    className={`${
                      checking ? 'ri-loader-4-line animate-spin' : 'ri-radar-line'
                    } mr-2`}
                  ></i>
                  {checking ? 'Comprobando...' : 'Comprobar URLs'}
                </button>

                {result && (
                  <div className="space-y-2">
                    {result.rows.map((r) => (
                      <div
                        key={r.key}
                        className={`text-sm rounded-lg px-4 py-3 border ${
                          r.ok
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-red-50 text-red-700 border-red-200'
                        }`}
                      >
                        <p className="font-medium">
                          <i
                            className={`${
                              r.ok ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'
                            } mr-2`}
                          ></i>
                          {r.label}
                        </p>
                        <p className="mt-1">{r.detail}</p>
                      </div>
                    ))}
                    <p className="text-xs text-gray-500">
                      La llamada sale del servidor de contabird. Confirma que el dominio resuelve,
                      que el TLS es válido y que el proxy llega hasta el servicio; no puede
                      descartar un cortafuegos que solo deje pasar tráfico interno.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
